<script>
export default {
  name: "DidoMillesimeDownloadCustom",
};
</script>

<script setup>
import { ref, computed } from "vue";
import DidoMillesimeInputFilterWrapper from "../filters/Wrapper.vue";
import api from "../../../api";
import { copyLinkToClipboard } from "../../../utils";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  rid: { type: String, required: true },
  millesime: { type: String, required: true },
  infos: { type: Object, required: true },
});

const parquetEnabled = ref(false);
api.withParquet(props.apiUrl).then((result) => (parquetEnabled.value = result));

// define reactive datas
const outputFormat = ref(null);
const headers = ref([]);
const geoField = ref(null);
const sortFieldTemp = ref(null);
const sortDirectionTemp = ref(null);
const sortClauses = ref([]);
const filterFieldTemp = ref(null);
const filterTypeTemp = ref(null);
const filterValueTemp = ref(null);
const filterClauses = ref([]);
const columnClauses = ref([]);

// define a ref for columnsSelection element
const columnsSelectionEl = ref(null);

// define a ref for the root element of our component
const customDownloadEl = ref(null);

// availables output formats
const optionsOutputFormat = computed(() => {
  const formats = [
    ...["csv", "xlsx", "json"],
    ...(parquetEnabled.value ? ["parquet"] : []),
    ...(props.infos.geoFields.length ? ["geojson"] : []),
  ];
  return formats.map((item) => ({ value: item, label: item }));
});

// headers options
const optionsHeaders = [
  {
    value: "name",
    label: "Ajouter une ligne d'entête avec le nom des colonnes",
  },
  {
    value: "description",
    label: "Ajouter une ligne d'entête avec la description des colonnes",
  },
  {
    value: "unit",
    label: "Ajouter une ligne d'entête avec l'unité des colonnes",
  },
];

// availables geoFields options
const optionsGeoField = computed(() => {
  if (!props.infos.geoFields.length) return [];
  return props.infos.geoFields.map((item) => ({ value: item, label: item }));
});

// show headers selection ?
const showHeadersSelection = computed(() => {
  if (!outputFormat.value) return false;
  if (["csv", "xlsx"].includes(outputFormat.value)) return true;
  return false;
});

// show geoField selection ?
const showGeoFieldSelection = computed(() => {
  if (outputFormat.value !== "geojson") return false;
  if (props.infos.geoFields.length === 1) return false;
  return true;
});

// show Sort, Filter and Columns clauses selection ?
const showSortFilterColumnClausesSelection = computed(() => {
  if (!outputFormat.value) return false;
  if (outputFormat.value === "parquet") return false;
  if (outputFormat.value === "geojson" && !geoField.value) return false;
  return true;
});

// show box with download link
const showLinks = computed(() => {
  if (!outputFormat.value) return false;
  if (outputFormat.value === "geojson" && !geoField.value) return false;
  return true;
});

// availables Sort Fields
const availableSortFields = computed(() => {
  return props.infos.columns
    .map((c) => c.name)
    .filter((name) => !sortClauses.value.find(({ field }) => field === name));
});

// available filters fields (merging columns and extened filters)
const availableFilterFields = computed(() => {
  const allFields = [
    ...props.infos.columns.map(({ name }) => name),
    ...props.infos.extendedFilters.map(({ name }) => name),
  ];
  return allFields.filter(
    (name) => !filterClauses.value.find(({ field }) => field === name),
  );
});
// available filters for a specific field
const availableFiltersForField = (field) => {
  // if field is a column => return filters of column
  const column = props.infos.columns.find(({ name }) => name === field);
  if (column)
    return column.filters.map((filterId) => ({
      value: filterId,
      label: api.filters[filterId],
    }));
  // if field is an extendedFilter => return filters of extendedFilter
  const extendedFilter = props.infos.extendedFilters.find(
    ({ name }) => name === field,
  );
  if (extendedFilter)
    return extendedFilter.filters.map((filterId) => ({
      value: filterId,
      label: api.filters[filterId],
    }));
  // finally
  return [];
};
// eventually there are more params for filter
const moreParamsFilter = (colName, type) => {
  const o = {};
  const columnSpecs = props.infos.columns.find(({ name }) => name === colName);
  if (!columnSpecs) return {};
  const filterInOrNin = columnSpecs.filters.find((filter) => filter === type);
  if (
    filterInOrNin &&
    columnSpecs.availableValues &&
    columnSpecs.availableValues.length
  )
    o.availableValues = columnSpecs.availableValues;
  return o;
};

// interaction when outputformat is selected
const outputFormatSelected = (format) => {
  if (format === "geojson") {
    if (props.infos.geoFields.length === 1)
      geoField.value = props.infos.geoFields[0];
  } else {
    geoField.value = null;
  }
};

// interaction with sort Clauses
const addSortClause = () => {
  sortClauses.value.push({
    field: sortFieldTemp.value,
    direction: sortDirectionTemp.value,
  });
  sortFieldTemp.value = null;
  sortDirectionTemp.value = null;
};
const removeSortClause = (index) => {
  sortClauses.value.splice(index, 1);
};

// interaction with filters Clauses
const changeFilterFieldTemp = () => {
  filterTypeTemp.value = null;
  filterValueTemp.value = null;
};
const changeFilterTypeTemp = () => {
  filterValueTemp.value = null;
};
const addFilterClause = () => {
  filterClauses.value.push({
    field: filterFieldTemp.value,
    type: filterTypeTemp.value,
    value: ["in", "nin"].includes(filterTypeTemp.value)
      ? filterValueTemp.value.split("$SEPARATOR$").join(",")
      : filterValueTemp.value,
  });
  filterFieldTemp.value = null;
  filterTypeTemp.value = null;
  filterValueTemp.value = null;
};
const removeFilterClause = (index) => {
  filterClauses.value.splice(index, 1);
};

// computed for download link
const downloadLink = computed(() => {
  let spec;
  if (outputFormat.value === "csv") {
    spec = {
      format: "csv",
      headers: {
        withColumnName: !!headers.value.find((item) => item === "name"),
        withColumnDescription: !!headers.value.find(
          (item) => item === "description",
        ),
        withColumnUnit: !!headers.value.find((item) => item === "unit"),
      },
      sort: sortClauses.value.map(({ field, direction }) => ({
        field,
        order: direction,
      })),
      filters: filterClauses.value.map(({ field, type, value }) => ({
        field,
        name: type,
        value,
      })),
      columns: columnClauses.value,
    };
  }
  if (outputFormat.value === "xlsx") {
    spec = {
      format: "xlsx",
      headers: {
        withColumnName: !!headers.value.find((item) => item === "name"),
        withColumnDescription: !!headers.value.find(
          (item) => item === "description",
        ),
        withColumnUnit: !!headers.value.find((item) => item === "unit"),
      },
      sort: sortClauses.value.map(({ field, direction }) => ({
        field,
        order: direction,
      })),
      filters: filterClauses.value.map(({ field, type, value }) => ({
        field,
        name: type,
        value,
      })),
      columns: columnClauses.value,
    };
  }
  if (outputFormat.value === "json") {
    spec = {
      format: "json",
      sort: sortClauses.value.map(({ field, direction }) => ({
        field,
        order: direction,
      })),
      filters: filterClauses.value.map(({ field, type, value }) => ({
        field,
        name: type,
        value,
      })),
      columns: columnClauses.value,
    };
  }
  if (outputFormat.value === "parquet") {
    spec = {
      format: "parquet",
      sort: [],
      filters: [],
      columns: [],
    };
  }
  if (outputFormat.value === "geojson") {
    spec = {
      format: "spatial/geojson",
      geoField: geoField.value,
      sort: sortClauses.value.map(({ field, direction }) => ({
        field,
        order: direction,
      })),
      filters: filterClauses.value.map(({ field, type, value }) => ({
        field,
        name: type,
        value,
      })),
      columns: columnClauses.value,
    };
  }
  return api.getDownloadUrl(props.apiUrl, props.rid, props.millesime, spec);
});
</script>

<template>
  <div ref="customDownloadEl">
    <!-- Select output format -->
    <QCard class="q-mt-md" square flat bordered>
      <QCardSection class="q-pa-sm">
        <div class="dido-millesime-title-group-download">
          Choix du format de sortie
        </div>
      </QCardSection>
      <QCardSection class="q-pa-xs">
        <QOptionGroup
          v-model="outputFormat"
          :options="optionsOutputFormat"
          color="secondary"
          inline
          @update:model-value="outputFormatSelected"
        />
      </QCardSection>
    </QCard>
    <!-- Select headers -->
    <QCard v-if="showHeadersSelection" class="q-mt-md" square flat bordered>
      <QCardSection class="q-pa-sm">
        <div class="dido-millesime-title-group-download">
          Personnalisation des entêtes
        </div>
      </QCardSection>
      <QCardSection class="q-pa-xs">
        <QOptionGroup
          v-model="headers"
          :options="optionsHeaders"
          color="secondary"
          type="checkbox"
        />
      </QCardSection>
    </QCard>
    <!-- Select geofield -->
    <QCard v-if="showGeoFieldSelection" class="q-mt-md" square flat bordered>
      <QCardSection class="q-pa-sm">
        <div class="dido-millesime-title-group-download">
          Choix du champ pivot pour l'export geojson
        </div>
      </QCardSection>
      <QCardSection class="q-pa-xs">
        <QOptionGroup
          v-model="geoField"
          :options="optionsGeoField"
          color="secondary"
          inline
        />
      </QCardSection>
    </QCard>
    <!-- Order Clauses -->
    <QCard
      v-if="showSortFilterColumnClausesSelection"
      class="q-mt-md"
      square
      flat
      bordered
    >
      <QCardSection class="q-pa-sm">
        <div class="dido-millesime-title-group-download">
          Gestion des critères de tri
        </div>
      </QCardSection>
      <QCardSection v-if="sortClauses.length" class="q-pa-sm">
        <QList dense class="fit-content">
          <QItem
            v-for="(sortClause, index) of sortClauses"
            :key="`sortClauses-${index}`"
          >
            <QItemSection side>{{ index + 1 }}</QItemSection>
            <QItemSection>
              <QChip size="sm" color="secondary" text-color="white">
                Tri
                {{
                  sortClause.direction === "asc" ? "croissant" : "décroissant"
                }}
                par {{ sortClause.field }}
              </QChip>
            </QItemSection>
            <QItemSection side>
              <QIcon
                name="mdi-delete-outline"
                color="secondary"
                size="xs"
                class="cursor-pointer q-ml-md"
                :title="`Supprimer le critère de tri sur la colonne ${sortClause.field}`"
                @click="removeSortClause(index)"
              />
            </QItemSection>
          </QItem>
        </QList>
      </QCardSection>
      <QCardSection class="q-pa-xs">
        <div
          v-if="availableSortFields.length"
          class="row-start-center q-col-gutter-sm"
        >
          <div class="col-auto">
            <div class="text-small">Ajouter un champ pour trier</div>
            <QSelect
              v-model="sortFieldTemp"
              dense
              options-dense
              square
              outlined
              hide-label
              :options="availableSortFields"
            />
          </div>
          <div v-if="sortFieldTemp" class="col-auto">
            <div class="text-small">Sélectionner l'ordre de tri</div>
            <QSelect
              v-model="sortDirectionTemp"
              dense
              options-dense
              square
              outlined
              :options="[
                { label: 'croissant', value: 'asc' },
                { label: 'décroissant', value: 'desc' },
              ]"
              hide-label
              emit-value
              map-options
            />
          </div>
          <div v-if="sortDirectionTemp" class="col-auto self-end">
            <QBtn
              outline
              size="sm"
              color="secondary"
              icon="mdi-plus-thick"
              label="Ajouter le critère de tri"
              :title="`Ajouter le critère de tri sur la colonne ${sortFieldTemp}`"
              @click="addSortClause"
            />
          </div>
        </div>
      </QCardSection>
    </QCard>
    <!-- Filter Clauses -->
    <QCard
      v-if="showSortFilterColumnClausesSelection"
      class="q-mt-md"
      square
      flat
      bordered
    >
      <QCardSection class="q-pa-sm">
        <div class="dido-millesime-title-group-download">
          Gestion des critères de filtrage
        </div>
      </QCardSection>
      <QCardSection v-if="filterClauses.length" class="q-pa-sm">
        <QList dense class="fit-content">
          <QItem
            v-for="(filterClause, index) of filterClauses"
            :key="`filterClauses-${index}`"
          >
            <QItemSection side>{{ index + 1 }}</QItemSection>
            <QItemSection>
              <QChip size="sm" color="secondary" text-color="white">
                {{ filterClause.field }} {{ api.filters[filterClause.type] }}
                {{
                  filterClause.type !== "withinGeometry"
                    ? filterClause.value
                    : ""
                }}
              </QChip>
            </QItemSection>
            <QItemSection side>
              <QIcon
                name="mdi-delete-outline"
                color="secondary"
                size="xs"
                class="cursor-pointer q-ml-md"
                :title="`Supprimer le critère le filtrage sur la colonne ${filterClause.field}`"
                @click="removeFilterClause(index)"
              />
            </QItemSection>
          </QItem>
        </QList>
      </QCardSection>
      <QCardSection class="q-pa-xs">
        <div
          v-if="availableFilterFields.length"
          class="row-start-center q-col-gutter-sm"
        >
          <div class="col-auto">
            <div class="text-small">Ajouter un champ pour filtrer</div>
            <QSelect
              v-model="filterFieldTemp"
              dense
              options-dense
              square
              outlined
              hide-label
              :options="availableFilterFields"
              @update:model-value="changeFilterFieldTemp"
            />
          </div>
          <div v-if="filterFieldTemp" class="col-auto">
            <div class="text-small">Sélectionner un type de filtre</div>
            <QSelect
              v-model="filterTypeTemp"
              dense
              options-dense
              square
              outlined
              :options="availableFiltersForField(filterFieldTemp)"
              hide-label
              emit-value
              map-options
              @update:model-value="changeFilterTypeTemp"
            />
          </div>
          <div v-if="filterTypeTemp" class="col-auto">
            <div class="text-small">Valeur pour filtrer</div>
            <DidoMillesimeInputFilterWrapper
              v-model="filterValueTemp"
              :label="false"
              :filter="filterTypeTemp"
              :api-url="props.apiUrl"
              :more-params="moreParamsFilter(filterFieldTemp, filterTypeTemp)"
              clearable
              clear-icon="mdi-close"
            />
          </div>
          <div v-if="filterValueTemp" class="col-auto self-end">
            <QBtn
              outline
              size="sm"
              color="secondary"
              icon="mdi-plus-thick"
              label="Ajouter le critère de filtrage"
              :title="`Ajouter le critère le filtrage sur le champ ${filterFieldTemp}`"
              @click="addFilterClause"
            />
          </div>
        </div>
      </QCardSection>
    </QCard>
    <!-- Column Clauses -->
    <QCard
      v-if="showSortFilterColumnClausesSelection"
      class="q-mt-md"
      square
      flat
      bordered
    >
      <QCardSection class="q-pa-sm">
        <div class="dido-millesime-title-group-download">
          Séléction des colonnes
        </div>
      </QCardSection>
      <QCardSection class="q-pa-sm row-start-center q-col-gutter-sm">
        <div class="col-auto">
          <QSelect
            ref="columnsSelectionEl"
            v-model="columnClauses"
            dense
            options-dense
            square
            borderless
            multiple
            filter
            hide-underline
            hide-bottom-space
            hide-dropdown-icon
            align="left"
            color="secondary"
            label-color="secondary"
            :options="
              props.infos.columns.map(({ name }) => ({
                value: name,
                label: name,
              }))
            "
            emit-value
          >
            <template #selected>
              <span v-if="columnClauses.length === 0" class="text-small">
                Aucune colonne sélectionnée : toutes les colonnes seront
                exportées
              </span>
              <span v-else>
                <QChip
                  v-for="(selectedColumn, index) of columnClauses"
                  :key="`selectedColumn-${index}`"
                  size="sm"
                  color="secondary"
                  text-color="white"
                >
                  {{ selectedColumn }}
                </QChip>
              </span>
            </template>
            <template #option="{ itemProps, opt, selected, toggleOption }">
              <QItem v-bind="itemProps" class="row-all-center">
                <QItemSection>
                  <QItemLabel class="text-small">{{ opt.label }}</QItemLabel>
                </QItemSection>
                <QItemSection side style="height: 15px">
                  <QToggle
                    :model-value="selected"
                    color="secondary"
                    dense
                    size="xs"
                    @update:model-value="toggleOption(opt)"
                  />
                </QItemSection>
              </QItem>
            </template>
            <template #after>
              <div class="row-start-center">
                <span class="on-left text-small">|</span>
                <QBtn
                  size="sm"
                  flat
                  color="primary"
                  dense
                  no-caps
                  title="Modifier le choix des colonnes"
                  @click="columnsSelectionEl.showPopup()"
                >
                  Modifier la sélection
                </QBtn>
              </div>
            </template>
          </QSelect>
        </div>
      </QCardSection>
    </QCard>
    <!-- Download link -->
    <div v-if="showLinks" class="q-mt-md row-start-center q-col-gutter-lg">
      <QBtn
        :href="downloadLink"
        target="_blank"
        title="Télécharger votre fichier de données personnalisé"
        label="Télécharger le fichier de données"
        flat
        no-caps
        size="sm"
        color="primary"
        icon="mdi-download"
        dense
      />
      <QBtn
        title="Copier le lien de téléchargement de votre fichier de données dans le presse-papier"
        label="Copier le lien"
        flat
        no-caps
        size="sm"
        color="primary"
        icon="mdi-clipboard-outline"
        dense
        @click="copyLinkToClipboard(downloadLink, customDownloadEl)"
      />
    </div>
  </div>
</template>

<style lang="sass" scoped>
.dido-millesime-title-group-download
  font-weight: 600
  font-size: 1.2em
</style>
